/**
 * The design system.
 *
 * Small on purpose. Every control here is sized from the variables in
 * `styles/tokens.css`, so density is a property of the system rather than something
 * each screen has to remember. If a screen needs a one-off size, that is a sign the
 * component is missing a variant, not that the screen should hard-code a pixel.
 */

import type {
  ReactNode,
  ReactElement,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  SelectHTMLAttributes,
} from 'react'
import { cloneElement, forwardRef, isValidElement, useEffect, useId, useRef, useState } from 'react'
import type { Tone } from './statusTone'
import { setToastHandler, type ToastTone } from './toast'
import { Icon } from './icons'
import './ui.css'

/* ------------------------------------------------------------------ button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, children, className = '', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={`tc-btn tc-btn-${variant} tc-btn-${size} ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  )
})

/** An icon-only button. The label is still required - it becomes the accessible
 *  name and the tooltip, so nothing on screen is a mystery shape. */
export function IconButton({
  label,
  children,
  ...rest
}: ButtonProps & { label: string }) {
  return (
    <Button aria-label={label} title={label} className="tc-btn-icon" {...rest}>
      {children}
    </Button>
  )
}

/* ------------------------------------------------------------------ fields */

type FieldProps = { label: string; hint?: string; error?: string; children: ReactNode; id?: string }

/**
 * A label, a control, and the help under it.
 *
 * The hint and the error sit *outside* the `<label>` and are attached with
 * `aria-describedby` instead. Inside it they became part of the control's
 * accessible name, so a field called "Retention" announced as "Retention Papers
 * you archive are kept for this many days before deletion" - the whole
 * explanation read out before the person could answer. A name says what it is;
 * a description explains it, and a screen reader lets you skip it.
 */
export function Field({ label, hint, error, children, id }: FieldProps) {
  const generated = useId()
  const controlId = id ?? generated
  const describedBy = error || hint ? `${controlId}-desc` : undefined

  return (
    <div className="tc-field">
      <label className="tc-field-label" htmlFor={controlId}>
        {label}
      </label>
      <FieldControl controlId={controlId} describedBy={describedBy}>
        {children}
      </FieldControl>
      {error ? (
        <span className="tc-field-error" id={describedBy} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="tc-field-hint" id={describedBy}>
          {hint}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Wires the control to its label and its description.
 *
 * The field used to be one big `<label>` wrapped around everything, which
 * associated the control implicitly - and swept the hint into its accessible
 * name at the same time. Splitting them means the association has to be explicit,
 * and every call site passes the control as `children` rather than by id, so it
 * is made here. Cloning is what keeps `<Field>` a one-liner at all of its call
 * sites; a control that already carries its own `id` keeps it.
 */
function FieldControl({
  controlId,
  describedBy,
  children,
}: {
  controlId: string
  describedBy?: string
  children: ReactNode
}) {
  if (!isValidElement(children)) return <>{children}</>
  const props = children.props as { id?: string; 'aria-describedby'?: string }
  return cloneElement(children as ReactElement<{ id?: string; 'aria-describedby'?: string }>, {
    id: props.id ?? controlId,
    'aria-describedby': [props['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined,
  })
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...rest }, ref) {
    return <input ref={ref} className={`tc-input ${className}`} {...rest} />
  },
)

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  InputHTMLAttributes<HTMLTextAreaElement> & { rows?: number }
>(function Textarea({ className = '', rows = 2, ...rest }, ref) {
  return <textarea ref={ref} rows={rows} className={`tc-input tc-textarea ${className}`} {...rest} />
})

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = '', children, ...rest }, ref) {
    return (
      <select ref={ref} className={`tc-input tc-select ${className}`} {...rest}>
        {children}
      </select>
    )
  },
)

/**
 * A password field with a reveal control.
 *
 * Typing a password you cannot see, on a laptop keyboard in a staffroom, is how
 * people end up locked out of an account they know the password for. The toggle
 * is a button rather than a checkbox so it is one press, and it never leaves the
 * field: the value is only visible while it is held on.
 */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordInput({ className = '', ...rest }, ref) {
  const [shown, setShown] = useState(false)
  return (
    <span className="tc-password">
      <input
        ref={ref}
        type={shown ? 'text' : 'password'}
        className={`tc-input tc-password-input ${className}`}
        {...rest}
      />
      <button
        type="button"
        className="tc-password-toggle"
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        title={shown ? 'Hide password' : 'Show password'}
        onClick={() => setShown((value) => !value)}
      >
        {shown ? <Icon.eyeOff size={16} /> : <Icon.eye size={16} />}
      </button>
    </span>
  )
})

export function Checkbox({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="tc-check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  )
}

export function Radio({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <label className="tc-check">
      <input type="radio" {...rest} />
      <span>{label}</span>
    </label>
  )
}

/* -------------------------------------------------------------------- pill */

export function Pill({ children, tone = 'muted' }: { children: ReactNode; tone?: Tone }) {
  return <span className={`tc-pill tc-pill-${tone}`}>{children}</span>
}

/* -------------------------------------------------------------------- tabs */

/**
 * The keyboard half of the tab pattern.
 *
 * The app already had the semantics right - `role="tablist"`, `aria-selected`,
 * `aria-controls` - but every tab was its own tab stop, so clearing the six-tab
 * settings strip took six presses of Tab and arrow keys did nothing. WAI-ARIA
 * says a tablist is *one* stop and the arrows move within it.
 *
 * Spread the result onto the element carrying `role="tablist"`; it drives the
 * tabs underneath it by DOM order, so no screen has to list them twice.
 */
export function useTabList() {
  const ref = useRef<HTMLDivElement>(null)

  const tabsIn = () =>
    [...(ref.current?.querySelectorAll<HTMLElement>('[role="tab"]:not(:disabled)') ?? [])]

  // Only the selected tab is reachable by Tab; the rest are reached with arrows.
  useEffect(() => {
    const tabs = tabsIn()
    tabs.forEach((tab) => {
      tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1
    })
    // No selected tab (a screen mid-load) would trap the keyboard entirely.
    if (tabs.length && !tabs.some((tab) => tab.tabIndex === 0)) tabs[0].tabIndex = 0
  })

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End']
    if (!keys.includes(event.key)) return
    const tabs = tabsIn()
    const current = tabs.indexOf(document.activeElement as HTMLElement)
    if (current === -1) return
    event.preventDefault()
    const next =
      event.key === 'Home' ? 0
      : event.key === 'End' ? tabs.length - 1
      : event.key === 'ArrowRight' ? (current + 1) % tabs.length
      : (current - 1 + tabs.length) % tabs.length
    // Activate on arrow, which is the expected behaviour for tabs whose panels
    // are already loaded - and every panel here is.
    tabs[next].focus()
    tabs[next].click()
  }

  return { ref, onKeyDown }
}

/* ------------------------------------------------------------------ layout */

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="tc-toolbar">{children}</div>
}

export function Spacer() {
  return <div className="tc-spacer" />
}

export function PageHeader({
  title,
  description,
  eyebrow,
  icon,
  actions,
}: {
  title: ReactNode
  description?: ReactNode
  eyebrow?: ReactNode
  icon?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="tc-page-header">
      <div className="tc-page-header-main">
        {icon && <span className="tc-page-header-icon" aria-hidden="true">{icon}</span>}
        <div className="tc-page-header-copy">
          {eyebrow && <span className="tc-page-header-eyebrow">{eyebrow}</span>}
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
      </div>
      {actions && <div className="tc-page-header-actions">{actions}</div>}
    </header>
  )
}

export function HeaderMetrics({
  label,
  items,
}: {
  label: string
  items: Array<{ value: ReactNode; label: ReactNode; tone?: 'default' | 'warning' }>
}) {
  return (
    <div className="tc-header-metrics" aria-label={label}>
      {items.map((item, index) => (
        <div className={item.tone === 'warning' ? 'is-warning' : ''} key={index}>
          <strong>{item.value}</strong>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Empty states teach rather than apologise, and stay small.
 *
 * The usual mistake is a half-screen illustration; on a work tool that is space
 * taken from the thing the person came to do.
 */
export function EmptyState({
  title,
  message,
  action,
  icon,
}: {
  title: string
  message: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="tc-empty">
      {icon && <span className="tc-empty-icon" aria-hidden="true">{icon}</span>}
      <h3>{title}</h3>
      <p>{message}</p>
      {action}
    </div>
  )
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="tc-spinner" role="status">
      <span className="tc-sr-only">{label}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ dialog */

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
  className,
}: {
  open: boolean
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  className?: string
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    // showModal gives focus trapping, Escape and the backdrop for free, rather
    // than three hand-rolled effects that each get one case wrong.
    if (open && !element.open) {
      element.showModal()
      const body = element.querySelector<HTMLElement>('.tc-dialog-body')
      if (body) body.scrollTop = 0
    }
    if (!open && element.open) element.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      className={['tc-dialog', className].filter(Boolean).join(' ')}
      onCancel={onClose}
      onClose={onClose}
    >
      <header className="tc-dialog-head">
        <h2>{title}</h2>
        <IconButton label="Close" variant="ghost" onClick={onClose}>
          <Icon.close size={16} />
        </IconButton>
      </header>
      <div className="tc-dialog-body">{children}</div>
      {footer && <footer className="tc-dialog-foot">{footer}</footer>}
    </dialog>
  )
}

/**
 * A destructive action asks once, in place.
 *
 * The confirming button is solid red rather than the violet primary: the confirm
 * step of a delete must not look exactly like "Save".
 */
export function ConfirmButton({
  label,
  confirmLabel = 'Yes, delete',
  message = 'This cannot be undone.',
  onConfirm,
}: {
  label: string
  confirmLabel?: string
  message?: string
  onConfirm: () => void
}) {
  const [asking, setAsking] = useState(false)

  if (!asking) {
    return (
      <Button variant="danger" onClick={() => setAsking(true)}>
        {label}
      </Button>
    )
  }

  return (
    <span className="tc-confirm">
      <span className="tc-confirm-text">{message}</span>
      <Button
        variant="danger"
        onClick={() => {
          setAsking(false)
          onConfirm()
        }}
      >
        {confirmLabel}
      </Button>
      {/* Cancel is primary: it is what most people want after pausing to read. */}
      <Button variant="primary" onClick={() => setAsking(false)}>
        Cancel
      </Button>
    </span>
  )
}

/* ----------------------------------------------------------------- popover */

export function Popover({
  trigger,
  label,
  children,
}: {
  trigger: ReactNode
  /** Names the panel for a screen reader. Without it the `dialog` announces as
   *  an unnamed group and gives no clue what opened. */
  label?: string
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const id = useId()

  useEffect(() => {
    if (!open) return
    const away = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  return (
    <div className="tc-popover-wrap" ref={ref}>
      <button
        type="button"
        className="tc-btn tc-btn-secondary tc-btn-md"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
      >
        {trigger}
      </button>
      {open && (
        <div
          className="tc-popover"
          id={id}
          role="dialog"
          aria-label={label ?? (typeof trigger === 'string' ? trigger : undefined)}
        >
          {children}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ toasts */

type Toast = { id: number; message: string; tone: ToastTone }

export function Toaster() {
  const [items, setItems] = useState<Toast[]>([])

  useEffect(() => {
    setToastHandler((message, tone = 'info') => {
      const id = Date.now() + Math.random()
      setItems((current) => [...current, { id, message, tone }])
      // Errors stay longer: they usually need reading twice.
      setTimeout(() => setItems((current) => current.filter((t) => t.id !== id)), tone === 'error' ? 6000 : 3000)
    })
    return () => setToastHandler(null)
  }, [])

  return (
    // `assertive` for errors would interrupt a screen reader mid-sentence; polite
    // is right for something that is informational and self-dismissing.
    <div className="tc-toaster" role="status" aria-live="polite">
      {items.map((item) => (
        <div key={item.id} className={`tc-toast tc-toast-${item.tone}`}>
          {item.message}
        </div>
      ))}
    </div>
  )
}
